import { rebillAmount, roundCents } from "../../expenses/shares.ts";
import { eur, parseNumber, pct } from "./format";
import type { ExpenseRow, ExpenseRuleRow, ShareSpec } from "./types";

export interface ShareLine { key: string; clientId: number | null; amountText: string; marginText: string; rebillRef: string; note: string; billable: boolean }
export interface Evaluation {
  error: string | null;
  fixedSum: number;
  restAmount: number | null;
  generalAmount: number | null;
  rebillTotal: number | null;
  specs: ShareSpec[];
}

export function newLine(clientId: number | null = null): ShareLine {
  return { key: crypto.randomUUID(), clientId, amountText: "", marginText: "", rebillRef: "", note: "", billable: true };
}

const deNum = (n: number) => String(n).replace(".", ",");

export function linesFromRow(row: ExpenseRow): ShareLine[] {
  if (row.classification !== "customer" || row.shares.length === 0) return [newLine()];
  return row.shares.map((s) => ({
    key: crypto.randomUUID(), clientId: s.client_id, amountText: deNum(s.amount), marginText: deNum(s.margin_pct),
    rebillRef: s.rebill_ref ?? "", note: s.note ?? "", billable: s.billable === 1,
  }));
}

export function linesFromRule(rule: ExpenseRuleRow): ShareLine[] {
  if (rule.shares.length === 0) return [newLine()];
  return rule.shares.map((s) => ({
    key: crypto.randomUUID(), clientId: s.client_id, amountText: s.amount === null ? "" : deNum(s.amount), marginText: deNum(s.margin_pct),
    rebillRef: s.rebill_ref ?? "", note: s.note ?? "", billable: s.billable === 1,
  }));
}

function fail(error: string): Evaluation {
  return { error, fixedSum: 0, restAmount: null, generalAmount: null, rebillTotal: null, specs: [] };
}

export function evaluate(lines: ShareLine[], total: number | null, defaultMarginPct: number): Evaluation {
  if (lines.length === 0) return fail("Mindestens ein Kunde ist nötig.");
  const specs: ShareSpec[] = [];
  let restCount = 0;
  for (const l of lines) {
    if (l.clientId === null) return fail("Kunde fehlt.");
    const spec: ShareSpec = { clientId: l.clientId };
    if (l.amountText.trim() !== "") {
      const a = parseNumber(l.amountText);
      if (a === null || roundCents(a) <= 0) return fail("Betrag ungültig.");
      spec.amount = roundCents(a);
    } else {
      restCount++;
    }
    if (l.marginText.trim() !== "") {
      const m = parseNumber(l.marginText);
      if (m === null || m < 0) return fail("Marge ungültig.");
      spec.marginPct = m;
    }
    if (l.rebillRef.trim()) spec.rebillRef = l.rebillRef.trim();
    if (l.note.trim()) spec.note = l.note.trim();
    if (!l.billable) spec.billable = false; // nur erfassen survives a re-save
    specs.push(spec);
  }
  if (restCount > 1) return fail("Nur eine Zeile darf „Rest“ sein.");
  const fixedSum = roundCents(specs.reduce((s, x) => s + (x.amount ?? 0), 0));
  let restAmount: number | null = null;
  let generalAmount: number | null = null;
  if (total !== null) {
    if (fixedSum > total + 0.005) return fail("Anteile übersteigen den Betrag.");
    const left = roundCents(total - fixedSum);
    if (restCount === 1) {
      if (left <= 0) return fail("Für „Rest“ bleibt nichts übrig.");
      restAmount = left;
      generalAmount = 0;
    } else {
      generalAmount = left;
    }
  }
  let rebillTotal: number | null = 0;
  for (const s of specs) {
    const margin = s.marginPct ?? defaultMarginPct;
    const amount = s.amount ?? restAmount;
    if (amount === null || amount === undefined) { rebillTotal = null; break; }
    rebillTotal += rebillAmount(amount, margin);
  }
  return { error: null, fixedSum, restAmount, generalAmount, rebillTotal: rebillTotal === null ? null : roundCents(rebillTotal), specs };
}

export function assignmentText(row: ExpenseRow): string {
  if (row.classification === "general") return "Allgemein";
  if (row.classification !== "customer") return "offen";
  const parts = row.shares.map((s) => `${s.client_name} ${eur(s.amount)}`);
  if (row.general_amount > 0.004) parts.push(`Rest ${eur(row.general_amount)}`);
  return parts.join(" · ");
}

export function ruleSharesText(rule: ExpenseRuleRow): string {
  if (rule.classification !== "customer") return "Allgemein";
  return rule.shares
    .map((s) => `${s.client_name} ${s.amount === null ? "Rest" : eur(s.amount)} +${pct(s.margin_pct)}${s.rebill_ref ? ` → ${s.rebill_ref}` : ""}`)
    .join(" · ");
}

export function sharedPayee(rows: ExpenseRow[]): string | null {
  const payees = new Set(rows.map((r) => r.payee));
  return rows.length > 0 && payees.size === 1 ? rows[0].payee : null;
}

/** Bank purpose lines often just repeat the payee — only show them when they add information. */
export function informativePurpose(row: ExpenseRow): string | null {
  const purpose = row.purpose?.trim();
  if (!purpose) return null;
  const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9äöüß]/g, "");
  const p = norm(purpose), payee = norm(row.payee);
  return p === payee || payee.includes(p) || p.includes(payee) ? null : purpose;
}
